import {
  BookSize,
  getSizesData,
  MAX_PHOTO_BOOK_PAGES,
} from '@mixtiles/web-backend-shared'
import type { QuestionProps } from '../../components/FaqSection/consts'
import { PhotoBookTheme } from '../../app/utils/productPages/types'
import { isImperialUnitsCountry } from '../SizesManager/SizesManagerHelper'
import { type CoverColors } from './coverColorsApi'

export const DEFAULT_PHOTO_RATIO = 1
export const DEFAULT_COVER_TEXT_KEY = 'photo_book.default_cover_text'
export const DEFAULT_BOOK_SIZE = BookSize.BOOK_10X10
export const COVER_TEXT_MAX_LENGTH = 25
export const PAGE_TEXT_MAX_LENGTH = 35

// Fallback when AI color extraction is unavailable, so a cover always carries
// colors (the print path requires them).
export const DEFAULT_AI_COVER_COLORS: CoverColors = {
  backgroundColor: '#ffffff',
  textColor: '#1a1a1a',
}
export const CAPTION_LENGTH_ZOOM_THRESHOLD = 15

// when the user crosses this threshold, the crop will actually start.
// when he is below this threshold, the crop will not start, enabling a user to easily fit the photo to the page
// During first zoom, while the photo already fits the page, we want to ignore this "resistance"
export const CROP_RESISTANCE_PERCENTAGE = 0.8

const MAX_PHOTOS_TRANSLATE_PARAMS: Record<string, string> = {
  max_photos: MAX_PHOTO_BOOK_PAGES.toString(),
}

interface PhotoBookFAQParams {
  getSizeName: (size: BookSize) => string
  getPrice: (size: BookSize) => string
  deliveryTime: string
  pricingCountry?: string
  isPriceTierListEnabled: boolean
  variant?: {
    size?: BookSize
    theme?: PhotoBookTheme
  }
}

function getBookDimensions(bookSize: BookSize, pricingCountry: string): string {
  const isImperial = isImperialUnitsCountry(pricingCountry)
  const sizesData = getSizesData()
  const sizeInfo = sizesData[bookSize] || sizesData[BookSize.BOOK_8X8]

  return isImperial ? sizeInfo.longDisplayName : sizeInfo.longDisplayNameCms
}

function createSizeParams(
  getSizeName: (size: BookSize) => string,
  getPrice?: (size: BookSize) => string,
  options?: {
    pricingCountry?: string
  }
): Record<string, string> {
  const params: Record<string, string> = {
    v1: getSizeName(BookSize.BOOK_8X8),
    v2: getSizeName(BookSize.BOOK_10X10),
    v3: getSizeName(BookSize.BOOK_12X12),
  }

  if (getPrice) {
    params.v1_price = getPrice(BookSize.BOOK_8X8)
    params.v2_price = getPrice(BookSize.BOOK_10X10)
    params.v3_price = getPrice(BookSize.BOOK_12X12)
  }

  if (options?.pricingCountry) {
    params.v1_dimensions = getBookDimensions(
      BookSize.BOOK_8X8,
      options.pricingCountry
    )
    params.v2_dimensions = getBookDimensions(
      BookSize.BOOK_10X10,
      options.pricingCountry
    )
    params.v3_dimensions = getBookDimensions(
      BookSize.BOOK_12X12,
      options.pricingCountry
    )
  }

  return params
}

const getWeddingFAQs = ({
  getSizeName,
}: PhotoBookFAQParams): QuestionProps[] => {
  const translateParamsSize = createSizeParams(getSizeName)

  return [
    {
      translateKey: 'photo-books-wedding-size',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-wedding-paper',
    },
    {
      translateKey: 'photo-books-wedding-pages',
    },
    {
      translateKey: 'photo-books-wedding-durability',
    },
    {
      translateKey: 'photo-books-wedding-vs-traditional',
    },
  ]
}

const getMemoryFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-memory-what-is',
    },
    {
      translateKey: 'photo-books-memory-create',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-memory-occasions',
    },
    {
      translateKey: 'photo-books-memory-custom',
    },
    {
      translateKey: 'photo-books-memory-pages',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-memory-cover',
    },
    {
      translateKey: 'photo-books-memory-cost',
    },
    {
      translateKey: 'photo-books-memory-easiest',
    },
  ]
}

const getSmallFAQs = ({
  getSizeName,
  pricingCountry = 'us',
}: PhotoBookFAQParams): QuestionProps[] => {
  const translateParamsSize = createSizeParams(getSizeName, undefined, {
    pricingCountry,
  })

  return [
    {
      translateKey: 'photo-books-small-sizes',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-small-why-choose',
    },
    {
      translateKey: 'photo-books-small-occasions',
    },
    {
      translateKey: 'photo-books-small-pages',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-small-customizable',
    },
    {
      translateKey: 'photo-books-small-gifts',
    },
    {
      translateKey: 'photo-books-small-materials',
    },
    {
      translateKey: 'photo-books-small-story',
    },
  ]
}

const getBoudoirFAQs = ({
  getSizeName,
}: PhotoBookFAQParams): QuestionProps[] => {
  const translateParamsSize = createSizeParams(getSizeName)

  return [
    {
      translateKey: 'photo-books-boudoir-what-is',
    },
    {
      translateKey: 'photo-books-boudoir-select-photos',
    },
    {
      translateKey: 'photo-books-boudoir-layout',
    },
    {
      translateKey: 'photo-books-boudoir-size-style',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-boudoir-personalize',
    },
    {
      translateKey: 'photo-books-boudoir-cover-materials',
    },
    {
      translateKey: 'photo-books-boudoir-color-mix',
    },
    {
      translateKey: 'photo-books-boudoir-order',
    },
  ]
}

const getTravelFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-travel-why-make',
    },
    {
      translateKey: 'photo-books-travel-features',
    },
    {
      translateKey: 'photo-books-travel-cropping',
    },
    {
      translateKey: 'photo-books-travel-details',
    },
    {
      translateKey: 'photo-books-travel-structure',
    },
    {
      translateKey: 'photo-books-travel-quality',
    },
    {
      translateKey: 'photo-books-travel-services',
    },
    {
      translateKey: 'photo-books-travel-relive',
    },
  ]
}

const getCoffeeTableFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-coffee-table-what-is',
    },
    {
      translateKey: 'photo-books-coffee-table-themes',
    },
    {
      translateKey: 'photo-books-coffee-table-design',
    },
    {
      translateKey: 'photo-books-coffee-table-travel-wedding',
    },
    {
      translateKey: 'photo-books-coffee-table-gifts',
    },
    {
      translateKey: 'photo-books-coffee-table-year-review',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-coffee-table-guest-house',
    },
    {
      translateKey: 'photo-books-coffee-table-suitable',
    },
  ]
}

const getEngagementFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-engagement-what-is',
    },
    {
      translateKey: 'photo-books-engagement-photos-include',
    },
    {
      translateKey: 'photo-books-engagement-proposal',
    },
    {
      translateKey: 'photo-books-engagement-party',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-engagement-vs-wedding',
    },
    {
      translateKey: 'photo-books-engagement-cover-styles',
    },
    {
      translateKey: 'photo-books-engagement-story',
    },
    {
      translateKey: 'photo-books-engagement-keepsake',
    },
  ]
}

const getFamilyFAQs = ({
  getSizeName,
}: PhotoBookFAQParams): QuestionProps[] => {
  const translateParamsSize = createSizeParams(getSizeName)

  return [
    {
      translateKey: 'photo-books-family-why-create',
    },
    {
      translateKey: 'photo-books-family-formats',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-family-organize',
    },
    {
      translateKey: 'photo-books-family-memories',
    },
    {
      translateKey: 'photo-books-family-pages',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-family-cover-size',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-family-durable',
    },
    {
      translateKey: 'photo-books-family-story',
    },
  ]
}

const getYearlyFAQs = ({
  getSizeName,
}: PhotoBookFAQParams): QuestionProps[] => {
  const translateParamsSize = createSizeParams(getSizeName)

  return [
    {
      translateKey: 'photo-books-yearly-what-is',
    },
    {
      translateKey: 'photo-books-yearly-gather-photos',
    },
    {
      translateKey: 'photo-books-yearly-what-include',
    },
    {
      translateKey: 'photo-books-yearly-themes-layouts',
    },
    {
      translateKey: 'photo-books-yearly-formats',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-yearly-personalize-cover',
    },
    {
      translateKey: 'photo-books-yearly-why-make',
    },
    {
      translateKey: 'photo-books-yearly-how-often',
    },
  ]
}

const getValentinesFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-valentines-great-gift',
    },
    {
      translateKey: 'photo-books-valentines-unique-ideas',
    },
    {
      translateKey: 'photo-books-valentines-style',
    },
    {
      translateKey: 'photo-books-valentines-wedding-photos',
    },
    {
      translateKey: 'photo-books-valentines-focus',
    },
    {
      translateKey: 'photo-books-valentines-fun-personal',
    },
    {
      translateKey: 'photo-books-valentines-suitable-couples',
    },
    {
      translateKey: 'photo-books-valentines-gather-content',
    },
  ]
}

const getGraduationFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-graduation-why-create',
    },
    {
      translateKey: 'photo-books-graduation-what-photos',
    },
    {
      translateKey: 'photo-books-graduation-organize',
    },
    {
      translateKey: 'photo-books-graduation-how-many-photos',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-graduation-design-theme',
    },
    {
      translateKey: 'photo-books-graduation-quotes-messages',
    },
    {
      translateKey: 'photo-books-graduation-sort-upload',
    },
    {
      translateKey: 'photo-books-graduation-templates-layouts',
    },
  ]
}

const getBirthdayFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-birthday-important-moments',
    },
    {
      translateKey: 'photo-books-birthday-photograph-party',
    },
    {
      translateKey: 'photo-books-birthday-candid-vs-posed',
    },
    {
      translateKey: 'photo-books-birthday-details-include',
    },
    {
      translateKey: 'photo-books-birthday-tell-story',
    },
    {
      translateKey: 'photo-books-birthday-angle-focus',
    },
    {
      translateKey: 'photo-books-birthday-subjects-interest',
    },
    {
      translateKey: 'photo-books-birthday-how-many-photos',
    },
  ]
}

const getRetirementFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-retirement-how-create',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-retirement-when-start-planning',
    },
    {
      translateKey: 'photo-books-retirement-types-photos',
    },
    {
      translateKey: 'photo-books-retirement-coworkers-contribute',
    },
    {
      translateKey: 'photo-books-retirement-humor-personal-touches',
    },
    {
      translateKey: 'photo-books-retirement-layout-ideas',
    },
    {
      translateKey: 'photo-books-retirement-photo-quality',
    },
    {
      translateKey: 'photo-books-retirement-meaningful-gifts',
    },
  ]
}

const getFathersDayFAQs = ({
  getSizeName,
}: PhotoBookFAQParams): QuestionProps[] => {
  const translateParamsSize = createSizeParams(getSizeName)

  return [
    {
      translateKey: 'photo-books-fathers-day-great-gift',
    },
    {
      translateKey: 'photo-books-fathers-day-memories-include',
    },
    {
      translateKey: 'photo-books-fathers-day-make-special',
    },
    {
      translateKey: 'photo-books-fathers-day-involve-children',
    },
    {
      translateKey: 'photo-books-fathers-day-format-recommended',
    },
    {
      translateKey: 'photo-books-fathers-day-who-book-for',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-fathers-day-how-many-photos',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-fathers-day-nostalgic-photos',
    },
  ]
}

const getPetDogFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-pet-dog-create-personalized',
    },
    {
      translateKey: 'photo-books-pet-dog-photos-include',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-pet-dog-puppy-family-photos',
    },
    {
      translateKey: 'photo-books-pet-dog-choose-best-photos',
    },
    {
      translateKey: 'photo-books-pet-dog-why-make-album',
    },
    {
      translateKey: 'photo-books-pet-dog-templates-design-ideas',
    },
    {
      translateKey: 'photo-books-pet-dog-include-milestones',
    },
    {
      translateKey: 'photo-books-pet-dog-gift-idea',
    },
  ]
}

const getAnniversaryFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-anniversary-great-gift',
    },
    {
      translateKey: 'photo-books-anniversary-memories-include',
    },
    {
      translateKey: 'photo-books-anniversary-how-many-photos',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-anniversary-everyday-candid-moments',
    },
    {
      translateKey: 'photo-books-anniversary-personal-unique',
    },
    {
      translateKey: 'photo-books-anniversary-any-year-celebration',
    },
    {
      translateKey: 'photo-books-anniversary-photo-quality',
    },
    {
      translateKey: 'photo-books-anniversary-tell-love-story',
    },
  ]
}

const getMothersDayFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-mothers-day-perfect-gift',
    },
    {
      translateKey: 'photo-books-mothers-day-memories-include',
    },
    {
      translateKey: 'photo-books-mothers-day-personalize-book',
    },
    {
      translateKey: 'photo-books-mothers-day-creative-themes',
    },
    {
      translateKey: 'photo-books-mothers-day-shared-activities',
    },
    {
      translateKey: 'photo-books-mothers-day-how-far-back',
      translateParams: MAX_PHOTOS_TRANSLATE_PARAMS,
    },
    {
      translateKey: 'photo-books-mothers-day-quotes-messages',
    },
    {
      translateKey: 'photo-books-mothers-day-creation-steps',
    },
  ]
}

const getDealsFAQs = ({
  isPriceTierListEnabled,
}: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-deals-current-available',
    },
    {
      translateKey: 'photo-books-deals-apply-promo-code',
    },
    {
      translateKey: 'photo-books-deals-combine-multiple',
    },
    ...(isPriceTierListEnabled
      ? []
      : [{ translateKey: 'photo-books-deals-bigger-orders' }]),
    {
      translateKey: 'photo-books-deals-stay-updated',
    },
  ]
}

const getChristmasFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-christmas-why-make',
    },
    {
      translateKey: 'photo-books-christmas-photos-include',
    },
    {
      translateKey: 'photo-books-christmas-theme-book',
    },
    {
      translateKey: 'photo-books-christmas-decorations-captions',
    },
    {
      translateKey: 'photo-books-christmas-snowy-outdoor-photos',
    },
    {
      translateKey: 'photo-books-christmas-creative-ideas',
    },
    {
      translateKey: 'photo-books-christmas-meaningful-gift',
    },
    {
      translateKey: 'photo-books-christmas-creation-steps',
    },
  ]
}

const getRecipeFAQs = (_params: PhotoBookFAQParams): QuestionProps[] => {
  return [
    {
      translateKey: 'photo-books-recipe-what-is',
    },
    {
      translateKey: 'photo-books-recipe-old-family-recipes',
    },
    {
      translateKey: 'photo-books-recipe-themes',
    },
    {
      translateKey: 'photo-books-recipe-organize',
    },
    {
      translateKey: 'photo-books-recipe-layout-details',
    },
    {
      translateKey: 'photo-books-recipe-cooking-process-photos',
    },
    {
      translateKey: 'photo-books-recipe-family-heirloom',
    },
    {
      translateKey: 'photo-books-recipe-make-engaging',
    },
  ]
}

const get8X8FAQs = ({ getSizeName }: PhotoBookFAQParams): QuestionProps[] => {
  const sizeDisplay = getSizeName(BookSize.BOOK_8X8)

  const translateParamsSize: Record<string, string> = {
    size_display: sizeDisplay,
    ...MAX_PHOTOS_TRANSLATE_PARAMS,
  }

  return [
    {
      translateKey: 'photo-books-8x8-what-makes-special',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-8x8-mix-portrait-landscape',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-8x8-cover-options',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-8x8-good-occasions',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-8x8-pages-have',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-8x8-square-format-display',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-8x8-templates-styles',
    },
    {
      translateKey: 'photo-books-8x8-why-choose',
      translateParams: translateParamsSize,
    },
  ]
}

const get12X12FAQs = ({ getSizeName }: PhotoBookFAQParams): QuestionProps[] => {
  const sizeDisplay = getSizeName(BookSize.BOOK_12X12)

  const translateParamsSize: Record<string, string> = {
    size_display: sizeDisplay,
    ...MAX_PHOTOS_TRANSLATE_PARAMS,
  }

  return [
    {
      translateKey: 'photo-books-12x12-sizes-formats',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-12x12-pages-contain',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-12x12-cover-materials',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-12x12-lay-flat-hardcover',
    },
    {
      translateKey: 'photo-books-12x12-good-for',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-12x12-compare-smaller',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-12x12-price-range',
      translateParams: translateParamsSize,
    },
    {
      translateKey: 'photo-books-12x12-professional-portfolio',
      translateParams: translateParamsSize,
    },
  ]
}

export const getPhotoBookFAQs = (
  params: PhotoBookFAQParams
): QuestionProps[] => {
  const { variant } = params
  const { size, theme } = variant || {}

  // If we have a size but no theme, use size-specific FAQs
  if (size && !theme) {
    switch (size) {
      case BookSize.BOOK_8X8:
        return get8X8FAQs(params)
      case BookSize.BOOK_12X12:
        return get12X12FAQs(params)
      default:
        // Fall through to theme-based logic for other sizes
        break
    }
  }

  // If we have a theme, use theme-specific FAQs (theme takes precedence over size)
  switch (theme) {
    case PhotoBookTheme.WEDDING:
    case PhotoBookTheme.WEDDING_DESIGNED_COVERS:
      return getWeddingFAQs(params)
    case PhotoBookTheme.MEMORY:
      return getMemoryFAQs(params)
    case PhotoBookTheme.SMALL:
      return getSmallFAQs(params)
    case PhotoBookTheme.BOUDOIR:
      return getBoudoirFAQs(params)
    case PhotoBookTheme.TRAVEL:
    case PhotoBookTheme.DESIGNED_COVERS:
      return getTravelFAQs(params)
    case PhotoBookTheme.COFFEE_TABLE:
    case PhotoBookTheme.COFFEE_TABLE_DESIGNED_COVERS:
      return getCoffeeTableFAQs(params)
    case PhotoBookTheme.ENGAGEMENT:
      return getEngagementFAQs(params)
    case PhotoBookTheme.FAMILY:
    case PhotoBookTheme.FAMILY_DESIGNED_COVERS:
      return getFamilyFAQs(params)
    case PhotoBookTheme.YEARLY:
      return getYearlyFAQs(params)
    case PhotoBookTheme.VALENTINES:
      return getValentinesFAQs(params)
    case PhotoBookTheme.GRADUATION:
      return getGraduationFAQs(params)
    case PhotoBookTheme.BIRTHDAY:
    case PhotoBookTheme.BIRTHDAY_DESIGNED_COVERS:
      return getBirthdayFAQs(params)
    case PhotoBookTheme.RETIREMENT:
      return getRetirementFAQs(params)
    case PhotoBookTheme.FATHERS_DAY:
      return getFathersDayFAQs(params)
    case PhotoBookTheme.PET_DOG:
      return getPetDogFAQs(params)
    case PhotoBookTheme.ANNIVERSARY:
      return getAnniversaryFAQs(params)
    case PhotoBookTheme.MOTHERS_DAY:
      return getMothersDayFAQs(params)
    case PhotoBookTheme.DEALS:
      return getDealsFAQs(params)
    case PhotoBookTheme.CHRISTMAS:
    case PhotoBookTheme.CHRISTMAS_DESIGNED_COVERS:
      return getChristmasFAQs(params)
    case PhotoBookTheme.RECIPE:
      return getRecipeFAQs(params)
    default: {
      const { getSizeName, getPrice, deliveryTime, isPriceTierListEnabled } =
        params
      const translateParamsSize = {
        ...createSizeParams(getSizeName, getPrice),
        ...MAX_PHOTOS_TRANSLATE_PARAMS,
      }

      const translateParamsDelivery: Record<string, string> = {
        delivery_time: deliveryTime,
      }

      return [
        {
          translateKey: isPriceTierListEnabled
            ? 'photo-books-size-tiers'
            : 'photo-books-size',
          translateParams: translateParamsSize,
        },
        {
          translateKey: 'photo-books-customize',
        },
        {
          translateKey: 'photo-books-delivery',
          translateParams: translateParamsDelivery,
        },
        {
          translateKey: 'photo-books-material',
        },
        {
          translateKey: 'photo-books-discount',
        },
        {
          translateKey: 'photo-books-shipment',
        },
      ]
    }
  }
}
